import type { NoteView } from '../../data/conversations.store';
import type { ThreadMessage } from '../../models/conversation-view.types';

/**
 * A message bubble or an internal-note marker, placed on the conversation timeline.
 *
 * `isEndOfSequence` (message entries only) marks the last message in a run of consecutive
 * messages from the same sender. The template uses it to render the WhatsApp-style speech
 * tail only on the bottom bubble of each block, and to tighten vertical spacing on the rest
 * so continuation bubbles bunch upward into the tailed one.
 */
export type TimelineEntry =
  | { kind: 'message'; at: string; message: ThreadMessage; isEndOfSequence: boolean }
  | { kind: 'note'; at: string; note: NoteView };

/**
 * Chronological day-group: header label plus the entries that fall on that local date.
 * Grouping (rather than a flat list with inline day markers) lets each group act as the
 * sticky-positioning container for its own header, so the header pins to the top of the
 * viewport while the group is being scrolled and unpins as the next day's group arrives.
 */
export interface DayGroup {
  dayKey: string;
  label: string;
  entries: TimelineEntry[];
}

// Messages with no ISO timestamp (in-flight optimistic sends) sort to the end.
const MAX_SENTINEL = '￿';

/**
 * Merge messages and internal notes into a chronological timeline grouped by local day, so
 * the thread can render a discreet marker wherever a note was created (without leaking the
 * note's content into the customer-facing flow) and a sticky day-header per group.
 */
export function buildTimeline(messages: readonly ThreadMessage[], notes: readonly NoteView[]): DayGroup[] {
  const merged: TimelineEntry[] = [
    ...messages.map((message): TimelineEntry => ({
      kind: 'message',
      at: message.sentAtIso ?? MAX_SENTINEL,
      message,
      // Tagged in a backward pass after the sort; this default is overwritten before return.
      isEndOfSequence: true,
    })),
    ...notes.map((note): TimelineEntry => ({ kind: 'note', at: note.createdAtIso, note })),
  ];
  merged.sort((left, right) => (left.at < right.at ? -1 : left.at > right.at ? 1 : 0));

  // Walk backward: a message is end-of-sequence when the next entry isn't another message
  // from the same sender. A note marker between two same-sender messages still breaks the run.
  let nextSender: ThreadMessage['sender'] | null = null;
  for (let i = merged.length - 1; i >= 0; i--) {
    const entry = merged[i];
    if (entry.kind !== 'message') {
      nextSender = null;
      continue;
    }
    entry.isEndOfSequence = nextSender !== entry.message.sender;
    nextSender = entry.message.sender;
  }

  // Forward pass: bucket entries into day-groups in insertion order.
  const groups: DayGroup[] = [];
  let current: DayGroup | null = null;
  for (const entry of merged) {
    const key = dayKey(entry.at);
    if (!key) {
      // No usable timestamp (optimistic in-flight): attach to the latest group or create
      // an "agora" placeholder so it still renders.
      if (!current) {
        current = { dayKey: 'pending', label: 'Hoje', entries: [] };
        groups.push(current);
      }
      current.entries.push(entry);
      continue;
    }
    if (!current || current.dayKey !== key) {
      current = { dayKey: key, label: formatDayLabel(entry.at), entries: [] };
      groups.push(current);
    }
    current.entries.push(entry);
  }
  return groups;
}

function dayKey(iso: string): string {
  if (iso === MAX_SENTINEL) {
    return '';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function formatDayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (sameDay(d, today)) {
    return 'Hoje';
  }
  if (sameDay(d, yesterday)) {
    return 'Ontem';
  }

  const diffDays = Math.floor((today.getTime() - d.getTime()) / 86_400_000);
  if (diffDays > 0 && diffDays < 7) {
    return capitalize(new Intl.DateTimeFormat('pt-BR', { weekday: 'long' }).format(d));
  }

  if (d.getFullYear() === today.getFullYear()) {
    return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'long' }).format(d);
  }
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }).format(d);
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function capitalize(value: string): string {
  return value.length === 0 ? value : value[0].toUpperCase() + value.slice(1);
}
